import {
    Brightness4,
    Brightness7,
    PhotoCamera,
    VisibilityOff,
} from '@mui/icons-material';
import MenuIcon from '@mui/icons-material/Menu';
import { AppBar, Box, Button, IconButton, Menu, MenuItem, Switch, Toolbar, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { resolvePublicMediaUrl } from '../helpers/formatters';
import { applyName, CAPTURE_AVATAR_SRC } from '../helpers/captureMode';
import { isMasterUser } from '../helpers/authSession';
import { clearCachedFeatures, readCachedFeatures, resolveMenuItemState } from '../helpers/featureFlags';
import { buildPaywallUrl } from '../helpers/paywall';
import PremiumFeatureBadge from './PremiumFeatureBadge';
import NotificationInbox from './NotificationInbox';

const Header = ({ toggleSidebar, toggleDarkMode, darkMode, captureMode, toggleCaptureMode }) => {
    const [userName, setUserName] = useState('');
    const [userEmail, setUserEmail] = useState('');
    const [storeName, setStoreName] = useState('');
    const [profilePicture, setProfilePicture] = useState('');
    const [anchorEl, setAnchorEl] = useState(null);  // Controla o estado do menu
    const [isMaster, setIsMaster] = useState(isMasterUser());
    const [features, setFeatures] = useState(readCachedFeatures);
    const openMenu = Boolean(anchorEl);  // Verifica se o menu está aberto
    const navigate = useNavigate();

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);  // Abre o menu quando clicar no ícone
    };
    const handleMenuClose = () => {
        setAnchorEl(null);  // Fecha o menu
    };
    const handleMenuMyAccount = () => {
        setAnchorEl(null);
        navigate('/admin/my-account');
    };
    const handleMenuIntegrations = () => {
        setAnchorEl(null);
        if (integrationsState.premiumLocked) {
            navigate(buildPaywallUrl({ returnTo: '/admin/integrations', featureKey: 'integrations' }));
            return;
        }
        navigate('/admin/integrations');
    };
    const handleMenuSalesChannels = () => {
        setAnchorEl(null);
        navigate('/admin/sales-channels');
    };
    const handleMenuPaymentFees = () => {
        setAnchorEl(null);
        navigate('/admin/payment-fees');
    };

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            let user = JSON.parse(storedUser);
            setUserName(user.name ?? '');
            setUserEmail(user.email ?? '');
            setStoreName(user.store_name ?? '');
            setProfilePicture(user.profile_picture);
        }
    }, []);

    useEffect(() => {
        const onFeaturesUpdated = (event) => {
            if (event?.detail && typeof event.detail === 'object') {
                setFeatures(event.detail);
            }
        };
        const onUserUpdated = (event) => {
            if (typeof event?.detail?.is_master === 'boolean') {
                setIsMaster(event.detail.is_master);
            }
        };
        window.addEventListener('acompanha:features-updated', onFeaturesUpdated);
        window.addEventListener('acompanha:user-updated', onUserUpdated);
        return () => {
            window.removeEventListener('acompanha:features-updated', onFeaturesUpdated);
            window.removeEventListener('acompanha:user-updated', onUserUpdated);
        };
    }, []);

    const handleLogout = () => {
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        clearCachedFeatures();
        console.log("Saindo...");
        window.location.href = '/admin/login';
        handleMenuClose();
    };

    const getInitials = (name, email) => {
        const trimmed = typeof name === 'string' ? name.trim() : '';
        if (trimmed !== '') {
            const parts = trimmed.split(/\s+/).filter(Boolean);
            const first = parts[0]?.charAt(0).toUpperCase() ?? '';
            const second = parts.length > 1 ? parts[1].charAt(0).toUpperCase() : '';
            return (first + second) || '?';
        }
        const mail = typeof email === 'string' ? email.trim() : '';
        if (mail !== '') {
            return mail.charAt(0).toUpperCase();
        }
        return '?';
    };

    const location = useLocation();
    const quickCheckoutState = resolveMenuItemState(
        { featureFlag: 'quick_checkout' },
        { isMaster, features },
    );
    const integrationsState = resolveMenuItemState(
        { featureFlag: 'integrations' },
        { isMaster, features },
    );

    const handleQuickCheckoutClick = (event) => {
        if (quickCheckoutState.premiumLocked) {
            event.preventDefault();
            navigate(buildPaywallUrl({ returnTo: '/admin/quick-checkout', featureKey: 'quick_checkout' }));
        }
    };

    return (
        <AppBar position="sticky" sx={{ zIndex: 1201 }}>
            <Toolbar>
                <IconButton edge="start" color="inherit" onClick={toggleSidebar}>
                    <MenuIcon />
                </IconButton>

                <Typography
                    variant="h6"
                    component="a"
                    href="/"
                    onClick={(e) => {
                        e.preventDefault();
                        window.location.href = '/admin';
                    }}
                    sx={{
                        flexGrow: 1,
                        fontFamily: '"Curitiba", sans-serif',
                        fontWeight: 'lighter',
                        letterSpacing: "1px",
                        textDecoration: "none", // Remove sublinhado padrão do link
                        color: "inherit", // Mantém a cor do tema
                        cursor: "pointer", // Indica que é clicável
                    }}
                >
                    ACOMPANHA 3D
                </Typography>

                {/* Itens de navegação no Header */}
                <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'row', alignItems: 'center', mr: 3 }}>
                    {/*<Button*/}
                    {/*    color={location.pathname === '/admin' ? 'primary' : 'inherit'} // Destaca o item ativo*/}
                    {/*    component={Link}*/}
                    {/*    to="/admin"*/}
                    {/*    startIcon={<DashboardIcon/>}*/}
                    {/*    sx={{*/}
                    {/*        mr: 3,*/}
                    {/*        color: location.pathname === '/admin' ? (darkMode ? 'yellow' : 'blue') : 'inherit'  // Cor personalizada no modo claro e escuro*/}
                    {/*    }}*/}
                    {/*>*/}
                    {/*    Dashboard*/}
                    {/*</Button>*/}
                    {/*<Button*/}
                    {/*    color={location.pathname === '/admin/printers' ? 'primary' : 'inherit'}*/}
                    {/*    component={Link}*/}
                    {/*    to="/admin/printers"*/}
                    {/*    startIcon={<PrinterIcon/>}*/}
                    {/*    sx={{*/}
                    {/*        mr: 3,*/}
                    {/*        color: (*/}
                    {/*            location.pathname === '/admin/printers' ||*/}
                    {/*            location.pathname === '/admin/register-printer' ||*/}
                    {/*            /^\/admin\/edit-printer\/[^/]+$/.test(location.pathname)  // Verifica se a rota corresponde a '/edit-printer/:id'*/}
                    {/*        ) ? (darkMode ? 'yellow' : 'blue') : 'inherit'*/}
                    {/*    }}*/}
                    {/*>*/}
                    {/*    Impressoras*/}
                    {/*</Button>*/}
                    {/*<Button*/}
                    {/*    color={location.pathname === '/admin/materials' ? 'primary' : 'inherit'}*/}
                    {/*    component={Link}*/}
                    {/*    to="/admin/materials"*/}
                    {/*    startIcon={<Widgets/>}*/}
                    {/*    sx={{*/}
                    {/*        mr: 3,*/}
                    {/*        color: (*/}
                    {/*            location.pathname === '/admin/materials' ||*/}
                    {/*            location.pathname === '/admin/register-material' ||*/}
                    {/*            /^\/admin\/edit-material\/[^/]+$/.test(location.pathname)  // Verifica se a rota corresponde a '/edit-printer/:id'*/}
                    {/*        ) ? (darkMode ? 'yellow' : 'blue') : 'inherit'*/}
                    {/*    }}*/}
                    {/*>*/}
                    {/*    Materiais e Acessórios*/}
                    {/*</Button>*/}
                    {/*<Button*/}
                    {/*    color={location.pathname === '/admin/products' ? 'primary' : 'inherit'}*/}
                    {/*    component={Link}*/}
                    {/*    to="/admin/products"*/}
                    {/*    startIcon={<StorefrontIcon/>}*/}
                    {/*    sx={{*/}
                    {/*        mr: 3,*/}
                    {/*        color: location.pathname === '/admin/products' ? (darkMode ? 'yellow' : 'blue') : 'inherit'*/}
                    {/*    }}*/}
                    {/*>*/}
                    {/*    Produtos*/}
                    {/*</Button>*/}

                    {/*<Button*/}
                    {/*    color={location.pathname === '/admin/calculator' ? 'primary' : 'inherit'}*/}
                    {/*    component={Link}*/}
                    {/*    to="/admin/calculator"*/}
                    {/*    startIcon={<CalculateOutlined />}*/}
                    {/*    sx={{*/}
                    {/*        mr: 3,*/}
                    {/*        color: location.pathname === '/admin/calculator' ? (darkMode ? 'yellow' : 'blue') : 'inherit'*/}
                    {/*    }}*/}
                    {/*>*/}
                    {/*    Calculadora de preço*/}
                    {/*</Button>*/}
                    {/*<Button*/}
                    {/*    color={location.pathname === '/admin/checkouts' ? 'primary' : 'inherit'}*/}
                    {/*    component={Link}*/}
                    {/*    to="/admin/checkouts"*/}
                    {/*    startIcon={<ShoppingCart/>}*/}
                    {/*    sx={{*/}
                    {/*        mr: 3,*/}
                    {/*        color: location.pathname === '/admin/checkouts' ? (darkMode ? 'yellow' : 'blue') : 'inherit'*/}
                    {/*    }}*/}
                    {/*>*/}
                    {/*    Vendas em andamento*/}
                    {/*</Button>*/}
                    {quickCheckoutState.visible && (
                        <Button
                            component={Link}
                            to="/admin/quick-checkout"
                            startIcon={<PhotoCamera />}
                            onClick={handleQuickCheckoutClick}
                            sx={{
                                mr: 3,
                                color: location.pathname === '/admin/quick-checkout' ? (darkMode ? 'yellow' : 'white') : 'inherit'
                            }}
                        >
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                Quick Checkout
                                {quickCheckoutState.premiumLocked ? <PremiumFeatureBadge /> : null}
                            </Box>
                        </Button>
                    )}
                </Box>


                {/* Mobile: Apenas ícone do Quick Checkout */}
                <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
                    {quickCheckoutState.visible && (
                        <Tooltip title="Quick Checkout" arrow>
                            <IconButton
                                component={Link}
                                to="/admin/quick-checkout"
                                onClick={handleQuickCheckoutClick}
                                sx={{
                                    color: location.pathname === '/admin/quick-checkout' ? (darkMode ? 'yellow' : 'white') : 'inherit'
                                }}
                            >
                                <PhotoCamera />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>

                <NotificationInbox />

                <IconButton color="inherit" onClick={handleMenuOpen}>
                    {captureMode ? (
                        <img
                            src={CAPTURE_AVATAR_SRC}
                            alt=""
                            style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                objectFit: 'cover',
                            }}
                        />
                    ) : profilePicture ? (
                        <img
                            src={resolvePublicMediaUrl(profilePicture)}
                            alt="Foto de Perfil"
                            style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                objectFit: 'cover', // Garante que a imagem cubra toda a área
                            }}
                        />
                    ) : (
                        <div
                            style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                backgroundColor: 'rgba(241, 39, 17, 0.8)',
                                color: 'white',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                fontWeight: 'bold',
                                fontSize: '18px',
                            }}
                        >
                            {getInitials(userName, userEmail)}
                        </div>
                    )}
                </IconButton>

                {/* Menu suspenso */}
                <Menu
                    anchorEl={anchorEl}  // Posição do menu
                    open={openMenu}  // Condição de abertura
                    onClose={handleMenuClose}  // Fecha o menu ao clicar fora ou nas opções
                >
                    <MenuItem onClick={handleMenuClose}>Olá, {applyName(userName) || userName}</MenuItem>
                    <MenuItem onClick={handleMenuMyAccount}>Minha Conta</MenuItem>
                    <MenuItem onClick={handleMenuSalesChannels}>Canais de vendas</MenuItem>
                    <MenuItem onClick={handleMenuPaymentFees}>Taxas da maquininha</MenuItem>
                    {integrationsState.visible && (
                        <MenuItem onClick={handleMenuIntegrations}>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                Integrações
                                {integrationsState.premiumLocked ? <PremiumFeatureBadge /> : null}
                            </Box>
                        </MenuItem>
                    )}

                    {/* Switch de tema dentro do menu (desktop e mobile) */}
                    <MenuItem sx={{ justifyContent: 'space-between', minWidth: '200px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {darkMode ? <Brightness4 /> : <Brightness7 />}
                            <Typography sx={{ ml: 1 }}>
                                {darkMode ? 'Modo Claro' : 'Modo Escuro'}
                            </Typography>
                        </Box>
                        <Switch
                            checked={darkMode}
                            onChange={toggleDarkMode}
                            name="darkMode"
                            color="default"
                            inputProps={{ 'aria-label': 'dark mode toggle' }}
                        />
                    </MenuItem>
                    <MenuItem sx={{ justifyContent: 'space-between', minWidth: '220px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <VisibilityOff />
                            <Typography sx={{ ml: 1 }}>Modo captura</Typography>
                        </Box>
                        <Switch
                            checked={Boolean(captureMode)}
                            onChange={toggleCaptureMode}
                            name="captureMode"
                            color="default"
                            inputProps={{ 'aria-label': 'modo captura de vídeo' }}
                        />
                    </MenuItem>

                    {/*<MenuItem onClick={handleMenuClose}>Configurações</MenuItem>*/}
                    <MenuItem onClick={handleLogout}>Sair</MenuItem>
                </Menu>
            </Toolbar>
        </AppBar>
    );
};

export default Header;
